import { useEffect, useRef, useState } from 'react';
/** The chosen login address is never a cache key, persisted value or contact attestation. */
export function useFundingAuthentication(client, onSessionCreated) {
    const [session, setSession] = useState(null);
    const [authenticationRequested, setAuthenticationRequested] = useState(false);
    const [busy, setBusy] = useState(false);
    const [error, setError] = useState(false);
    const request = useRef(null);
    const disposed = useRef(false);
    const chosenEmail = useRef(null);
    useEffect(() => {
        disposed.current = false;
        const stop = () => {
            disposed.current = true;
            request.current?.abort();
            chosenEmail.current = null;
            setSession(null);
            setAuthenticationRequested(false);
            setBusy(false);
        };
        const unregister = client.onDispose(stop);
        return () => {
            unregister();
            stop();
        };
    }, [client]);
    async function createSession(email, version, base = false) {
        if (request.current || disposed.current)
            return;
        const controller = new AbortController();
        request.current = controller;
        chosenEmail.current = email ?? null;
        setBusy(true);
        setError(false);
        try {
            const next = base
                ? await client.createSession(controller.signal)
                : version === undefined
                    ? await client.createAuthenticationSession(email, controller.signal)
                    : await client.createAuthenticationSession(email, controller.signal, version);
            if (!controller.signal.aborted && !disposed.current) {
                client.observeLifecycle?.({
                    event: 'provider_replaced',
                    reason: base ? 'attestation_or_provider_refresh' : version ? 'terms' : 'authentication',
                });
                onSessionCreated?.();
                setAuthenticationRequested(!base);
                setSession(next);
            }
        }
        catch {
            if (!controller.signal.aborted && !disposed.current)
                setError(true);
        }
        finally {
            if (request.current === controller)
                request.current = null;
            if (!controller.signal.aborted && !disposed.current)
                setBusy(false);
        }
    }
    return {
        session,
        authenticationRequested,
        busy,
        error,
        authenticate(email) {
            return createSession(email.trim());
        },
        async acceptTerms(version) {
            if (!version)
                return;
            await createSession(chosenEmail.current ?? undefined, version);
        },
        replace() {
            return createSession(undefined, undefined, true);
        },
        reset() {
            request.current?.abort();
            request.current = null;
            chosenEmail.current = null;
            setSession(null);
            setAuthenticationRequested(false);
            setBusy(false);
            setError(false);
        },
    };
}
